import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, InputGroup, Alert, Spinner } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Badge } from "primereact/badge";
import { BsEyeSlash, BsEye, BsPersonPlus, BsShieldCheck, BsTrash } from "react-icons/bs";
import { FiUsers, FiUserPlus, FiRefreshCw, FiSearch } from "react-icons/fi";
import InfoHeader from "../components/InfoHeader";
import {
    createUser,
    confirmUser,
    resendConfirmationCode,
    listUsers,
    deleteUser,
    addStoredUser,
    updateStoredUserStatus
} from "../components/API/CognitoAdminApi";
import "../styles/UserManagement.css";

const UserManagement = () => {
    // Users list state
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        username: "",
        email: "",
        name: "",
        password: "",
        confirmPassword: "",
        role: "viewer"
    });

    // UI state
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Verification state
    const [showVerification, setShowVerification] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [createdUsername, setCreatedUsername] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);

    // Delete confirmation state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Password validation state
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecial: false
    });

    const roleOptions = [
        { label: "All Roles", value: null },
        { label: "Admin", value: "admin" },
        { label: "Viewer", value: "viewer" }
    ];

    // Fetch users on component mount
    const fetchUsers = useCallback(async () => {
        setIsLoadingUsers(true);
        try {
            const result = await listUsers();
            if (result.success) {
                setUsers(result.data);
                setFilteredUsers(result.data);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
        } finally {
            setIsLoadingUsers(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Filter users based on search term and role
    useEffect(() => {
        let filtered = users;

        if (searchTerm.trim() !== "") {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(user =>
                user.username?.toLowerCase().includes(term) ||
                user.email?.toLowerCase().includes(term) ||
                user.name?.toLowerCase().includes(term)
            );
        }

        if (roleFilter) {
            filtered = filtered.filter(user => user.role === roleFilter);
        }

        setFilteredUsers(filtered);
    }, [searchTerm, roleFilter, users]);

    // Validate password requirements
    useEffect(() => {
        const password = formData.password;
        setPasswordValidation({
            minLength: password.length >= 8,
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        });
    }, [formData.password]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError("");
    };

    const validateForm = () => {
        if (!formData.username || !formData.email || !formData.name || !formData.password) {
            setError("All fields are required");
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError("Please enter a valid email address");
            return false;
        }

        // Check for duplicate email
        const emailExists = users.some(
            user => user.email?.toLowerCase() === formData.email.toLowerCase()
        );
        if (emailExists) {
            setError("A user with this email address already exists");
            return false;
        }

        // Check for duplicate username
        const usernameExists = users.some(
            user => user.username?.toLowerCase() === formData.username.toLowerCase()
        );
        if (usernameExists) {
            setError("A user with this username already exists");
            return false;
        }

        const allPasswordReqsMet = Object.values(passwordValidation).every(v => v);
        if (!allPasswordReqsMet) {
            setError("Password does not meet all requirements");
            return false;
        }

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match");
            return false;
        }

        return true;
    };

    const resetForm = () => {
        setFormData({
            username: "",
            email: "",
            name: "",
            password: "",
            confirmPassword: "",
            role: "user"
        });
        setShowPassword(false);
        setShowConfirmPassword(false);
        setError("");
    };

    const handleCloseModal = () => {
        setShowCreateModal(false);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const result = await createUser({
                username: formData.username,
                email: formData.email,
                name: formData.name,
                password: formData.password,
                role: formData.role
            });

            if (result.success) {
                setSuccess(`User "${formData.username}" created successfully! User can now log in.`);
                setShowCreateModal(false);

                const newUser = {
                    username: formData.username,
                    email: formData.email,
                    name: formData.name,
                    role: formData.role,
                    status: 'CONFIRMED',
                    enabled: true,
                    createdAt: new Date().toISOString()
                };

                addStoredUser(newUser);
                setUsers(prev => [newUser, ...prev]);
                resetForm();

                // Refresh users list from API
                fetchUsers();
            }
        } catch (err) {
            setError(err.error || "Failed to create user. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerification = async (e) => {
        e.preventDefault();
        setError("");
        setIsVerifying(true);

        try {
            const result = await confirmUser(createdUsername, verificationCode);
            if (result.success) {
                setSuccess(`User "${createdUsername}" has been verified and can now log in.`);
                setShowVerification(false);
                setVerificationCode("");
                updateStoredUserStatus(createdUsername, 'CONFIRMED');
                setUsers(prev => prev.map(user =>
                    user.username === createdUsername
                        ? { ...user, status: 'CONFIRMED' }
                        : user
                ));
                setCreatedUsername("");
            }
        } catch (err) {
            setError(err.error || "Failed to verify user. Please check the code and try again.");
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendCode = async () => {
        setError("");
        setIsResending(true);

        try {
            await resendConfirmationCode(createdUsername);
            setSuccess("Verification code has been resent to the user's email.");
        } catch (err) {
            setError(err.error || "Failed to resend verification code.");
        } finally {
            setIsResending(false);
        }
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;

        setIsDeleting(true);
        try {
            const result = await deleteUser(userToDelete.username);
            if (result.success) {
                setUsers(prev => prev.filter(u => u.username !== userToDelete.username));
                setSuccess(`User "${userToDelete.username}" has been deleted.`);
            } else {
                setError(result.error || "Failed to delete user");
            }
        } catch (err) {
            setError("Failed to delete user. Please try again.");
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    };

    const PasswordRequirement = ({ met, text }) => (
        <div className={`password-requirement ${met ? 'met' : ''}`}>
            <span className="requirement-icon">{met ? '✓' : '○'}</span>
            <span>{text}</span>
        </div>
    );

    // Table column templates
    const serialNumberTemplate = (rowData, options) => {
        return <span>{options.rowIndex + 1}</span>;
    };

    const roleTemplate = (rowData) => {
        const role = rowData.role || 'viewer';
        if (role === 'admin') {
            return (
                <span className="role-badge role-admin">
                    <BsShieldCheck className="me-1" />
                    Admin
                </span>
            );
        }
        return <span className="role-badge role-user">Viewer</span>;
    };

    const statusTemplate = (rowData) => {
        const status = rowData.status || rowData.UserStatus;
        switch (status) {
            case 'CONFIRMED':
                return <span className="status-badge status-active">Active</span>;
            case 'UNCONFIRMED':
                return <span className="status-badge status-pending">Pending</span>;
            case 'DISABLED':
                return <span className="status-badge status-disabled">Disabled</span>;
            default:
                return <span className="status-badge status-unknown">{status || 'Unknown'}</span>;
        }
    };

    const dateTemplate = (rowData) => {
        const dateString = rowData.createdAt || rowData.UserCreateDate;
        if (!dateString) return '---';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const actionTemplate = (rowData) => {
        return (
            <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text"
                onClick={() => handleDeleteClick(rowData)}
                tooltip="Delete user"
                tooltipOptions={{ position: 'top' }}
            />
        );
    };

    const tableContainerStyle = {
        width: "100%",
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        padding: "15px",
        display: "flex",
        flexDirection: "column",
        minHeight: "400px",
        marginBottom: "20px",
    };

    const tableHeadingStyle = {
        textAlign: "center",
        fontWeight: "bold",
        fontSize: "18px",
        color: "#007BFF",
    };

    const filterContainerStyle = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "15px",
        flexWrap: "wrap",
        gap: "10px"
    };

    return (
        <div
            className="col-lg-12"
            style={{
                maxHeight: "90vh",
                overflowY: "scroll",
                overflowX: "hidden",
            }}
        >
            <InfoHeader />

            {/* Alerts */}
            {error && (
                <Alert variant="danger" onClose={() => setError("")} dismissible className="mx-3">
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" onClose={() => setSuccess("")} dismissible className="mx-3">
                    {success}
                </Alert>
            )}

            {/* Verification Card */}
            {showVerification && (
                <div className="card border mx-3 mb-3" style={{ borderColor: '#0dcaf0', backgroundColor: '#f0fafc' }}>
                    <div className="card-body">
                        <h5 className="text-info mb-3">Verify User Email</h5>
                        <p className="text-muted">
                            Enter the verification code sent to the user's email to activate their account.
                        </p>
                        <Form onSubmit={handleVerification}>
                            <Form.Group className="mb-3">
                                <Form.Label>Verification Code for <strong>{createdUsername}</strong></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter verification code"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value)}
                                    required
                                    style={{ maxWidth: '300px' }}
                                />
                            </Form.Group>
                            <div className="d-flex gap-2 flex-wrap">
                                <Button
                                    label={isVerifying ? "Verifying..." : "Verify User"}
                                    icon={isVerifying ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                    onClick={handleVerification}
                                    disabled={isVerifying || !verificationCode}
                                    className="p-button-info"
                                />
                                <Button
                                    label={isResending ? "Resending..." : "Resend Code"}
                                    icon="pi pi-refresh"
                                    onClick={handleResendCode}
                                    disabled={isResending}
                                    className="p-button-secondary"
                                />
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times"
                                    onClick={() => {
                                        setShowVerification(false);
                                        setCreatedUsername("");
                                        setVerificationCode("");
                                    }}
                                    className="p-button-outlined p-button-danger"
                                />
                            </div>
                        </Form>
                    </div>
                </div>
            )}

            {/* Main Card */}
            <div className="card border dashboard-card mx-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h2 className="fs-5 fw-semibold mb-0">
                        <FiUsers className="me-2" />
                        User Management
                    </h2>
                    <Button
                        label="Create User"
                        icon="pi pi-user-plus"
                        onClick={() => setShowCreateModal(true)}
                        className="p-button-primary"
                    />
                </div>
                <div className="card-body">
                    <div style={tableContainerStyle}>
                        {/* Filters */}
                        <div style={filterContainerStyle}>
                            <div className="d-flex align-items-center gap-2">
                                <span className="p-input-icon-left">
                                    <i className="pi pi-search" />
                                    <InputText
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search users..."
                                        style={{ width: '250px' }}
                                    />
                                </span>
                            </div>

                            <div style={tableHeadingStyle}>Users List</div>

                            <div className="d-flex align-items-center gap-2">
                                <span className="me-2">Filter by Role:</span>
                                <Dropdown
                                    value={roleFilter}
                                    options={roleOptions}
                                    onChange={(e) => setRoleFilter(e.value)}
                                    placeholder="Select Role"
                                    className="p-dropdown-sm"
                                    style={{ width: "150px" }}
                                />
                                <Button
                                    icon="pi pi-refresh"
                                    onClick={fetchUsers}
                                    disabled={isLoadingUsers}
                                    className="p-button-outlined"
                                    tooltip="Refresh"
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                        </div>

                        {/* DataTable */}
                        <DataTable
                            value={filteredUsers}
                            scrollable
                            scrollHeight="350px"
                            className="p-datatable-sm p-datatable-striped"
                            emptyMessage="No users found"
                            loading={isLoadingUsers}
                            responsiveLayout="scroll"
                        >
                            <Column
                                header="Sr. No."
                                body={serialNumberTemplate}
                                style={{ width: "8%" }}
                            />
                            <Column
                                field="username"
                                header="Username"
                                style={{ width: "15%" }}
                                bodyStyle={{ fontWeight: '500' }}
                            />
                            <Column
                                field="name"
                                header="Name"
                                style={{ width: "18%" }}
                                body={(rowData) => rowData.name || '---'}
                            />
                            <Column
                                field="email"
                                header="Email"
                                style={{ width: "22%" }}
                                body={(rowData) => rowData.email || '---'}
                            />
                            <Column
                                header="Role"
                                body={roleTemplate}
                                style={{ width: "12%" }}
                                bodyStyle={{ textAlign: "center" }}
                            />
                            <Column
                                header="Status"
                                body={statusTemplate}
                                style={{ width: "12%" }}
                                bodyStyle={{ textAlign: "center" }}
                            />
                            <Column
                                header="Created"
                                body={dateTemplate}
                                style={{ width: "13%" }}
                            />
                            <Column
                                header="Actions"
                                body={actionTemplate}
                                style={{ width: "8%" }}
                                bodyStyle={{ textAlign: "center" }}
                            />
                        </DataTable>

                        {/* Footer info */}
                        <div className="mt-3 text-muted small">
                            Showing {filteredUsers.length} of {users.length} users
                        </div>
                    </div>
                </div>
            </div>

            {/* Create User Modal */}
            <Modal
                show={showCreateModal}
                onHide={handleCloseModal}
                size="lg"
                centered
            >
                <Modal.Header closeButton style={{ backgroundColor: '#007bff', color: 'white' }}>
                    <Modal.Title>
                        <FiUserPlus className="me-2" />
                        Create New User
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible>
                            {error}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Username <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="username"
                                        placeholder="Enter username"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isLoading}
                                    />
                                    <Form.Text className="text-muted">This will be used to log in</Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        placeholder="Enter full name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isLoading}
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                placeholder="Enter email address"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                            />
                            <Form.Text className="text-muted">A verification email will be sent to this address</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                name="role"
                                value={formData.role}
                                onChange={handleInputChange}
                                disabled={isLoading}
                            >
                                <option value="viewer">Viewer</option>
                                <option value="admin">Admin</option>
                            </Form.Select>
                            <Form.Text className="text-muted">Admin users can create and manage other users</Form.Text>
                        </Form.Group>

                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            placeholder="Enter password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isLoading}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setShowPassword(!showPassword)}
                                            disabled={isLoading}
                                        >
                                            {showPassword ? <BsEyeSlash /> : <BsEye />}
                                        </button>
                                    </InputGroup>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            placeholder="Confirm password"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isLoading}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            disabled={isLoading}
                                        >
                                            {showConfirmPassword ? <BsEyeSlash /> : <BsEye />}
                                        </button>
                                    </InputGroup>
                                </Form.Group>
                            </div>
                        </div>

                        {/* Password Requirements */}
                        <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="card-body py-2">
                                <small className="text-muted d-block mb-2">Password Requirements:</small>
                                <div className="row">
                                    <div className="col-6">
                                        <PasswordRequirement met={passwordValidation.minLength} text="At least 8 characters" />
                                        <PasswordRequirement met={passwordValidation.hasUppercase} text="One uppercase letter" />
                                        <PasswordRequirement met={passwordValidation.hasLowercase} text="One lowercase letter" />
                                    </div>
                                    <div className="col-6">
                                        <PasswordRequirement met={passwordValidation.hasNumber} text="One number" />
                                        <PasswordRequirement met={passwordValidation.hasSpecial} text="One special character" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        onClick={handleCloseModal}
                        disabled={isLoading}
                        className="p-button-secondary"
                    />
                    <Button
                        label={isLoading ? "Creating..." : "Create User"}
                        icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-user-plus"}
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="p-button-primary"
                    />
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Header closeButton style={{ backgroundColor: '#dc3545', color: 'white' }}>
                    <Modal.Title>
                        <BsTrash className="me-2" />
                        Delete User
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete user <strong>{userToDelete?.username}</strong>?</p>
                    <p className="text-muted small">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={isDeleting}
                        className="p-button-secondary"
                    />
                    <Button
                        label={isDeleting ? "Deleting..." : "Delete"}
                        icon={isDeleting ? "pi pi-spin pi-spinner" : "pi pi-trash"}
                        onClick={handleDeleteConfirm}
                        disabled={isDeleting}
                        className="p-button-danger"
                    />
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default UserManagement;
